import { ref } from "vue";
import { useI18n } from "vue-i18n";
import { getSubscriptions, fetchSubscription, } from "../../infrastructure/adapters/subscriptionService";
import { useFinanceFormatters } from "@/shared/composables/useFinanceFormatters";
import { useAppToast } from "@/shared/composables/useAppToast";
import { logger } from "@/infrastructure/services/LoggerService";
/**
 * Shared subscription-picker data source for the donation create modal and the donation detail
 * tab. Owns the option list, loading flag and the localized label formatting so the
 * two call sites can't drift (the formatter and search were previously duplicated verbatim).
 */
export function useSubscriptionOptions() {
    const { t } = useI18n();
    const { formatAmount } = useFinanceFormatters();
    const { toastError } = useAppToast();
    const subscriptions = ref([]);
    const options = ref([]);
    const loading = ref(false);
    let loaded = false;
    function toOption(s) {
        const donor = s.donorName?.trim() || t("finance.subscriptions.unnamedDonor", { id: s.id.slice(0, 8) });
        const amount = formatAmount(s.amount, s.currency);
        const frequency = t(`finance.subscriptions.frequency.${s.frequency}`);
        return { value: s.id, label: `${donor} — ${amount} / ${frequency}` };
    }
    async function load(params = {}) {
        if (loaded && !params.force)
            return;
        loading.value = true;
        try {
            const res = await getSubscriptions({ donorId: params.donorId, page: 1, pageSize: 50 });
            subscriptions.value = res.result;
            const selectedId = params.ensureSelectedId;
            if (selectedId && !subscriptions.value.some((s) => s.id === selectedId)) {
                try {
                    subscriptions.value = [await fetchSubscription(selectedId), ...subscriptions.value];
                }
                catch (err) {
                    logger.error("useSubscriptionOptions: failed to load linked subscription", err);
                }
            }
            options.value = subscriptions.value.map(toOption);
            loaded = true;
        }
        catch (err) {
            logger.error("useSubscriptionOptions: failed to load subscriptions", err);
            toastError("finance.subscriptions.loadError");
        }
        finally {
            loading.value = false;
        }
    }
    function search(query) {
        const q = query.trim().toLowerCase();
        const matches = q
            ? subscriptions.value.filter((s) => toOption(s).label.toLowerCase().includes(q))
            : subscriptions.value;
        options.value = matches.map(toOption);
    }
    return { subscriptions, options, loading, load, search, toOption };
}
